import * as XLSX from "xlsx";

export interface IExporter {
  export(data: any): string;
}

export interface IFormatter<T> {
  format(data: T): any[];
}

export class CsvExporter implements IExporter {
  export(data: any[]): string {
    if (!data || data.length === 0) {
      return "";
    }

    // Extraire les en-têtes du premier élément
    const headers = Object.keys(data[0]);

    // Convertir les données en lignes CSV
    const csvRows = [];

    // Ajouter les en-têtes
    csvRows.push(headers.join(","));

    // Ajouter les lignes de données
    for (const row of data) {
      const values = headers.map((header) => {
        const value = row[header];
        // Échapper les virgules et guillemets
        const escaped =
          value !== null && value !== undefined
            ? String(value).replace(/"/g, "\"\"")
            : "";
        // Entourer de guillemets si nécessaire
        return `"${escaped}"`;
      });

      csvRows.push(values.join(","));
    }

    return csvRows.join("\n");
  }
}

// Formatteur de workorder
export class WorkorderFormatter implements IFormatter<EasyTech.Workorder> {
  format(workorder: EasyTech.Workorder): any[] {
    return [
      {
        id: workorder.id,
        reference: workorder.ref,
        customer: workorder.customer.name,
        status: workorder.status,
        createdAt: workorder.created,
        updatedAt: workorder.updated,
        type: "workorder",
      },
    ];
  }
}

// Formatteur de machines
export class MachinesFormatter
  implements IFormatter<EasyTech.WorkorderMachine[]> {
  format(machines: EasyTech.WorkorderMachine[]): any[] {
    return machines.map((machine) => ({
      id: machine.id,
      workorderId: (machine as any).workOrder?.id || "",
      machineId: machine.machine?.id || "",
      machineName: (machine.machine as any)?.name || "",
      brand: machine.machine?.brand || "",
      model: (machine.machine as any)?.model || "",
      serialNumber: machine.machine?.serialNumber || "",
      notes: machine.notes || "",
      type: "machine",
    }));
  }
}

// Formatteur de hoses
export class HosesFormatter implements IFormatter<EasyTech.WorkorderHose[]> {
  format(hoses: EasyTech.WorkorderHose[]): any[] {
    return hoses.map((hose) => ({
      id: hose.id,
      workorderId: hose.workOrder?.id || "",
      machineId: hose.machine?.id || "",
      name: hose.name || "",
      description: hose.description || "",
      type: "hose",
    }));
  }
}

// Formatteur de parts
export class PartsFormatter implements IFormatter<EasyTech.WoPart[]> {
  format(parts: EasyTech.WoPart[]): any[] {
    return parts.map((part) => ({
      id: part.id || "",
      workorderId: (part as any).workOrder?.id || "",
      machineId: part.machine?.id || "",
      hoseId: part.hose?.id || "",
      partId: part.part?.id || "",
      reference: part.part?.ref || "",
      label: part.part?.label || "",
      quantity: part.quantity || 0,
      price: part.price || 0,
      totalPrice: (part.quantity || 0) * (part.price || 0),
      type: "part",
    }));
  }
}

/**
 * Service d'exportation des données de workorder au format CSV/XLSX
 */
export class WorkorderExportService {
  private exporter: IExporter;

  // En-têtes du CSV pour le modèle d'export
  private readonly headers = [
    "Id", "Reference", "Customer's name", "Intervention's address", "Contact",
    "Technician's name", "Technician's mail", "Machine name", "Machine brand", "Machine model",
    "Hose name", "Hose reference", "Part name", "Part quantity", "Counter", "Price per unit",
    "Hose name (no machine)", "Hose reference", "Part name", "Part quantity", "Counter", "Price per unit",
    "Part name (no machine - no hose)", "Part quantity", "Counter", "Price per unit"
  ];

  constructor(exporter: IExporter = new CsvExporter()) {
    this.exporter = exporter;
  }

  /**
   * Exporte toutes les données du workorder dans un format hiérarchique (XLSX)
   * @param workorder Le workorder à exporter
   */
  exportWorkorderAsXlsx(workorder: EasyTech.Workorder): void {
    // Tableau pour stocker toutes les lignes
    const rows: string[][] = [];

    // Génération des lignes pour le workorder et ses composants
    this.addWorkorderInfoRow(rows, workorder);
    this.addMachinesRows(rows, workorder.machines || []);
    this.addWorkorderHosesRows(rows, workorder.hoses || []);
    this.addWorkorderPartsRows(rows, workorder.parts || []);

    // Ajout des en-têtes au début
    rows.unshift(this.headers);

    // Conversion en XLSX et téléchargement
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Intervention");

    XLSX.writeFile(workbook, `intervention-${workorder.ref}.xlsx`);
  }

  /**
   * Ajoute la ligne d'informations générales du workorder
   */
  private addWorkorderInfoRow(rows: string[][], workorder: EasyTech.Workorder): void {
    const baseRow = this.createEmptyRow();

    // Remplir les informations du workorder
    baseRow[0] = workorder.id || "";
    baseRow[1] = workorder.ref || "";
    baseRow[2] = workorder.customer?.name || "";
    baseRow[3] = typeof workorder.customer?.address === "string" ? workorder.customer.address : "";
    baseRow[4] = typeof workorder.customer?.contact === "string" ? workorder.customer.contact : "";
    baseRow[5] = workorder.technician?.firstName + " " + workorder.technician?.lastName || "";
    baseRow[6] = workorder.technician?.email || "";

    rows.push(baseRow);
  }

  /**
   * Ajoute les lignes pour les machines, leurs hoses et leurs parts
   */
  private addMachinesRows(rows: string[][], machines: EasyTech.WorkorderMachine[]): void {
    for (const machine of machines) {
      if (!machine.machine) continue;

      // Ligne pour la machine
      const machineRow = this.createEmptyRow();
      machineRow[7] = machine.machine?.serialNumber || "";
      machineRow[8] = machine.machine?.brand?.label || "";
      machineRow[9] = machine.machine?.type?.label || "";
      rows.push(machineRow);

      // Hoses liés à la machine
      this.addMachineHosesRows(rows, machine.hoses || []);

      // Parts liés directement à la machine (sans hose)
      this.addMachinePartsRows(rows, machine.parts || []);
    }
  }

  /**
   * Ajoute les lignes pour les hoses d'une machine et leurs parts
   */
  private addMachineHosesRows(rows: string[][], hoses: EasyTech.WorkorderHose[]): void {
    for (const hose of hoses) {
      // Ligne pour le hose
      const hoseRow = this.createEmptyRow();
      hoseRow[10] = hose.name || "";
      hoseRow[11] = hose.ref || "";
      rows.push(hoseRow);

      // Parts liés au hose
      this.addHosePartsRows(rows, hose.parts || [], 12); // Index 12 pour les parts de hose de machine
    }
  }

  /**
   * Ajoute les lignes pour les hoses du workorder (sans machine) et leurs parts
   */
  private addWorkorderHosesRows(rows: string[][], hoses: EasyTech.WorkorderHose[]): void {
    for (const hose of hoses) {
      // Ligne pour le hose
      const hoseRow = this.createEmptyRow();
      hoseRow[16] = hose.name || "";
      hoseRow[17] = hose.ref || "";
      rows.push(hoseRow);

      // Parts liés au hose
      this.addHosePartsRows(rows, hose.parts || [], 18); // Index 18 pour les parts de hose sans machine
    }
  }

  /**
   * Ajoute les lignes pour les parts d'un hose
   * @param startColumn Colonne de départ pour les données de parts (12 pour machine-hose ou 18 pour workorder-hose)
   */
  private addHosePartsRows(rows: string[][], parts: EasyTech.WoPart[], startColumn: number): void {
    for (const part of parts) {
      const partRow = this.createEmptyRow();
      partRow[startColumn] = part.part?.label || "";
      partRow[startColumn + 1] = part.quantity?.toString() || "";
      partRow[startColumn + 2] = "0";
      partRow[startColumn + 3] = part.price?.toString() || "-";
      rows.push(partRow);
    }
  }

  /**
   * Ajoute les lignes pour les parts directement liées à une machine
   */
  private addMachinePartsRows(rows: string[][], parts: EasyTech.WoPart[]): void {
    for (const part of parts) {
      const partRow = this.createEmptyRow();
      partRow[22] = part.part?.label || "";
      partRow[23] = part.quantity?.toString() || "";
      partRow[24] = "0";
      partRow[25] = part.price?.toString() || "-";
      rows.push(partRow);
    }
  }

  /**
   * Ajoute les lignes pour les parts directement liées au workorder
   */
  private addWorkorderPartsRows(rows: string[][], parts: EasyTech.WoPart[]): void {
    for (const part of parts) {
      const partRow = this.createEmptyRow();
      partRow[22] = part.part?.label || "";
      partRow[23] = part.quantity?.toString() || "";
      partRow[24] = "0";
      partRow[25] = part.price?.toString() || "-";
      rows.push(partRow);
    }
  }

  /**
   * Crée une ligne vide avec des tirets pour toutes les colonnes
   */
  private createEmptyRow(): string[] {
    return Array(this.headers.length).fill("-");
  }
}
